import type { Me } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiError, post, setCsrf } from './api/client';
import { keys } from './api/hooks';
import { Icon } from './components/ui';

/** «Забыли пароль?»: ссылка придёт в Telegram-бот или на подключённую почту; ответ одинаковый для любого адреса. */
function Forgot({ email: initial, back }: { email: string; back: () => void }) {
  const [email, setEmail] = useState(initial);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      aria-labelledby="forgot-title"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr('');
        try {
          await post('/auth/forgot', { email });
          setSent(true);
        } catch (x) {
          setErr(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Нет связи с сервером');
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="brand" id="forgot-title">
        <span className="bic">
          <Icon name="brief" size={18} />
        </span>
        Новый пароль
      </div>
      {sent ? (
        <p role="status" className="hint">
          Если такой адрес есть в трекере, ссылка для нового пароля придёт в ваш Telegram-бот, а если он не
          привязан — письмом на подключённую почту. Ссылка действует час. Ничего не пришло — напишите
          администратору: он может создать ссылку в «Пользователях».
        </p>
      ) : (
        <>
          <p className="hint">
            Укажите почту, с которой входите. Ссылку пришлём туда, куда подключён трекер: в Telegram-бот или
            на вашу почту.
          </p>
          <label className="f">
            Почта
            <input
              name="email"
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {err && (
            <p className="err" role="alert">
              {err}
            </p>
          )}
          <button className="btn primary wide" type="submit" disabled={busy || !email}>
            {busy ? 'Отправляю…' : 'Прислать ссылку'}
          </button>
        </>
      )}
      <button className="btn ghost wide" type="button" onClick={back}>
        Вернуться ко входу
      </button>
    </form>
  );
}

export function Login() {
  const qc = useQueryClient();
  const [forgot, setForgot] = useState<string | null>(null);
  const [needCode, setNeedCode] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setErr('');
    try {
      const me = await post<Me>('/auth/login', {
        email: String(f.get('email') ?? ''),
        password: String(f.get('password') ?? ''),
        ...(needCode ? { totp: String(f.get('totp') ?? '') } : {}),
      });
      setCsrf(me.csrfToken);
      qc.setQueryData(keys.me, me);
    } catch (x) {
      if (x instanceof ApiError) {
        const d = x.details as { totpRequired?: boolean } | undefined;
        if (d?.totpRequired) setNeedCode(true);
        setErr(x.status === 422 ? 'Проверьте почту и пароль' : x.message);
      } else setErr('Нет связи с сервером');
    } finally {
      setBusy(false);
    }
  };

  if (forgot != null)
    return (
      <main className="login">
        <Forgot email={forgot} back={() => setForgot(null)} />
      </main>
    );
  return (
    <main className="login">
      <form onSubmit={submit} aria-labelledby="login-title">
        <div className="brand" id="login-title">
          <span className="bic">
            <Icon name="brief" size={18} />
          </span>
          Поиск работы
        </div>
        <label className="f">
          Почта
          <input name="email" type="email" autoComplete="username" required autoFocus />
        </label>
        <label className="f">
          Пароль
          <input name="password" type="password" autoComplete="current-password" required />
        </label>
        {needCode && (
          <label className="f">
            Код из приложения
            <input
              name="totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              autoFocus
            />
          </label>
        )}
        {err && (
          <p className="err" role="alert">
            {err}
          </p>
        )}
        <button className="btn primary wide" type="submit" disabled={busy}>
          {busy ? 'Вхожу…' : 'Войти'}
        </button>
        <button
          className="btn ghost wide"
          type="button"
          onClick={(e) => {
            const v = (e.currentTarget.form?.elements.namedItem('email') as HTMLInputElement | null)?.value;
            setForgot(v ?? '');
          }}
        >
          Забыли пароль?
        </button>
      </form>
    </main>
  );
}
